import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useLocation } from 'react-router';
import { useLlmChatInquiryScope } from '@/hooks/useLlmChatInquiryScope';
import { useLlmChatConceptScope } from '@/hooks/useLlmChatConceptScope';

export type LlmChatOpenOptions = {
  sessionId?: string | null;
  initialMessage?: string;
  /** Uploaded attachment IDs to send with the first message (e.g. from dashboard widget) */
  initialAttachmentIds?: string[];
  /** `searchParams.get('agent')` when creating a session from the panel */
  agentQuery?: string | null;
  /** Explicit inquiry scope; pass `null` to clear override */
  inquiryId?: string | null;
};

type LlmChatDrawerContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  activeSessionId: string | null;
  setActiveSessionId: Dispatch<SetStateAction<string | null>>;
  pendingInitialMessage: string | undefined;
  setPendingInitialMessage: (v: string | undefined) => void;
  pendingInitialAttachmentIds: string[] | undefined;
  setPendingInitialAttachmentIds: (v: string[] | undefined) => void;
  agentQueryForNewSession: string | null;
  setAgentQueryForNewSession: (v: string | null) => void;
  /** Effective inquiry filter: explicit override or route-derived */
  scopeInquiryId: string | null;
  /** Project concept from `/inquiries/new?conceptId=...` */
  scopeConceptId: string | null;
  openChat: (opts?: LlmChatOpenOptions) => void;
};

const LlmChatDrawerContext = createContext<LlmChatDrawerContextValue | null>(null);

export function LlmChatDrawerProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [pendingInitialMessage, setPendingInitialMessage] = useState<string | undefined>();
  const [pendingInitialAttachmentIds, setPendingInitialAttachmentIds] = useState<string[] | undefined>();
  const [agentQueryForNewSession, setAgentQueryForNewSession] = useState<string | null>(null);
  const [explicitInquiryId, setExplicitInquiryId] = useState<string | null | undefined>(undefined);

  const routeInquiryId = useLlmChatInquiryScope();
  const scopeInquiryId =
    explicitInquiryId !== undefined ? explicitInquiryId : routeInquiryId;
  const scopeConceptId = useLlmChatConceptScope();

  // Auto-close the drawer on any navigation (including same-route, e.g. /inquiries/new
  // → /inquiries/new with new state). Watching location.key instead of pathname catches
  // those same-path navigations too. Click handlers can just call navigate(...) without
  // also poking drawer state - the close animation runs in parallel with the route
  // transition instead of being dispatched in the same render batch (which made the
  // new page render under the still-animating drawer).
  const location = useLocation();
  const lastKeyRef = useRef(location.key);
  useEffect(() => {
    if (lastKeyRef.current === location.key) return;
    lastKeyRef.current = location.key;
    setOpen(false);
  }, [location.key]);

  const openChat = useCallback((opts?: LlmChatOpenOptions) => {
    if (!opts) {
      setAgentQueryForNewSession(null);
      setOpen(true);
      return;
    }
    if ('sessionId' in opts) {
      setActiveSessionId(opts.sessionId ?? null);
    }
    if ('initialMessage' in opts) {
      setPendingInitialMessage(opts.initialMessage);
    }
    if ('initialAttachmentIds' in opts) {
      setPendingInitialAttachmentIds(opts.initialAttachmentIds);
    } else {
      setPendingInitialAttachmentIds(undefined);
    }
    if ('agentQuery' in opts) {
      setAgentQueryForNewSession(opts.agentQuery ?? null);
    } else {
      setAgentQueryForNewSession(null);
    }
    if ('inquiryId' in opts) {
      setExplicitInquiryId(opts.inquiryId ?? null);
    }
    setOpen(true);
  }, []);

  const value = useMemo(
    () => ({
      open,
      setOpen,
      activeSessionId,
      setActiveSessionId,
      pendingInitialMessage,
      setPendingInitialMessage,
      pendingInitialAttachmentIds,
      setPendingInitialAttachmentIds,
      agentQueryForNewSession,
      setAgentQueryForNewSession,
      scopeInquiryId,
      scopeConceptId,
      openChat,
    }),
    [
      open,
      activeSessionId,
      pendingInitialMessage,
      pendingInitialAttachmentIds,
      agentQueryForNewSession,
      scopeInquiryId,
      scopeConceptId,
      openChat,
    ],
  );

  return (
    <LlmChatDrawerContext.Provider value={value}>{children}</LlmChatDrawerContext.Provider>
  );
}

export function useLlmChatDrawer() {
  const ctx = useContext(LlmChatDrawerContext);
  if (!ctx) {
    throw new Error('useLlmChatDrawer must be used within LlmChatDrawerProvider');
  }
  return ctx;
}
